import type { AdminResourcesTranslationMessages } from './adminResourcesTranslationMessages';

export const adminResourcesLanguageEnglish: AdminResourcesTranslationMessages = {
  labels: {
    name: 'Labels',
    description: "Manage labels for resources' access control",
    helperText: 'List of access labels for this record',
    fields: {
      id: 'ID',
      delete: 'Delete',
      edit: 'Edit',
      name: 'Name',
      read: 'Read',
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create a label',
      delete: 'Delete the label',
      edit: 'Edit label',
    },
  },
  callflows: {
    name: 'Dialplans',
    fields: {
      id: 'ID',
      name: 'Name',
      number: 'Number (optional)',
      priority: 'Priority',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      entry: 'Entrypoint',
      is_backup: 'Backup',
      last_edit: 'Last edit',
      latest_callflow_version: 'Backup of',
      nodes: 'Nodes',
      assigned_inbound_e164s: 'Inbound E164',
    },
    operations: {
      create: 'Create callflow',
      edit: 'Edit callflow',
      delete: 'Delete callflow',
    },
    helperTexts: {
      number:
        'Optional number for the callflow. If set, can be used to call the callflow from internal extensions via feature code',
      priority:
        'The order in which the dialplan will be executed. The lower the number, the earlier it will be executed',
      assigned_inbound_e164s: 'Inbound numbers that will execute this callflow',
    },
  },
  mobility_configurations: {
    name: 'Mobility configurations',
    helperTexts: {
      owner: 'The owner of the record',
    },
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      owner: 'Owner',
    },
  },
  latest_callflow_version: {
    name: 'Dialplan backup',
  },
  carrier_mappings: {
    name: 'Carrier mappings',
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      interface: 'Interface',
      enabled: 'Enabled',
      labels: { id: 'Labels', name: 'Labels' },
      match_property: 'Match property',
      set_property: 'Set property',
      rules: 'Rules',
    },
  },
  user_mappings: {
    name: 'User mappings',
    fields: {
      id: 'ID',
      name: 'Name',
      interface: 'Interface',
      description: 'Description',
      enabled: 'Enabled',
      labels: { id: 'Labels', name: 'Labels' },
      match_property: 'Match property',
      set_property: 'Set property',
      rules: 'Rules',
    },
    user_inbound: 'User inbound',
    user_outbound: 'User outbound',
    all: 'All',
  },
  mappings: {
    name: 'Mappings',
    fields: {
      interface: 'Interface',
      id: 'ID',
      name: 'Name',
      description: 'Description',
      enabled: 'Enabled',
      match_property: 'Match property',
      enum_match_property: {
        caller_id_number: 'Caller ID number',
        caller_id_name: 'Caller ID name',
        called_id_name: 'Called ID name',
        called_id_number: 'Called ID number',
        remote_network_addr: 'Remote network address',
      },
      set_property: 'Set property',
      enum_set_property: {
        caller_id_number: 'Caller ID number',
        caller_id_name: 'Caller ID name',
        called_id_name: 'Called ID name',
        called_id_number: 'Called ID number',
        remote_network_addr: 'Remote network address',
      },
      rules: 'Rules',
      labels: { id: 'Labels', name: 'Labels' },
      match_rule: 'Match rule',
      set_rule: 'Set rule',
    },
    operations: {
      create: 'Create mapping',
      edit: 'Edit mapping',
      delete: 'Delete mapping',
      sections: {
        details: 'Details',
      },
    },
    helperTexts: {
      match_property: 'Call property to use for matching rules',
      set_property: 'Call property to set on match',
      rules: 'Match/Set rules, processed in order, first wins.',
    },
  },
  queues: {
    name: 'Queues',
    fields: {
      calendar: 'Calendar',
      call_recording_conf_id: 'Call recording configuration',
      callback_announce: 'Announce',
      callback_book_announce: 'Book announce',
      callback_success_announce: 'Success announce',
      customsound: 'Custom sound',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      id: 'ID',
      name: 'Name',
      type: 'Type',
      number: 'Number',
      settings: {
        alarm_threshold: 'Alarm threshold',
        announce_agent: 'Announce agent',
        announce_frequency: 'Announce frequency',
        announce_position: 'Announce position',
        announce_waiting: 'Announce waiting',
        busy_delay_time: 'Busy delay time',
        call_timeout: 'Call timeout',
        callback_caller_id_number: 'Callback caller id number',
        callback_caller_number_regex: 'Callback caller number regex',
        callback_delay: 'Callback delay',
        callback_enabled: 'Callback enabled',
        callback_retention: 'Callback retention',
        callback_retries: 'Callback retries',
        callback_timeout: 'Callback timeout',
        callback_username: 'Callback username',
        callback_wait_time: 'Callback wait time',
        continue_on_answer: 'Continue on answer',
        continue_on_max_waiters: 'Continue on max waiters',
        daily_reset_stats: 'Daily reset stats',
        daily_reset_stats_time: 'Daily reset stats time',
        exitkey: 'Exit using DTMF',
        ignore_unavail: 'Ignore unavail',
        max_exten: 'Max exten',
        max_wait_time: 'Max wait time',
        no_answer_delay_time: 'No answer delay time',
        reject_delay_time: 'Reject delay time',
        service_level: 'Service level',
        skip_empty: 'Skip empty',
        weight: 'Weight',
        wrap_up_time: 'Wrap up time',
      },
      soundfile: 'Soundfile',
      strategy: 'Strategy',
      strategies: {
        all: 'Ring all',
        rr: 'Round robin',
        fewest_calls: 'Fewest Calls',
        rr_memory: 'Round robin with memory',
        mi: 'Longest idle',
      },
    },
    operations: {
      create: 'Create queue',
      edit: 'Edit queue',
      delete: 'Delete queue',
      sections: {
        details: 'Details',
        settings: 'Settings',
        callbacks: 'Callbacks',
      },
    },
    helperTexts: {
      number: 'Number of the queue',
      strategy: 'How a call is dispatched to agents',
      callback_announce: 'The callback announcement soundfile',
      callback_book_announce: 'The callback book confirmation soundfile',
      callback_success_announce: 'The callback successful soundfile',
      settings: {
        alarm_threshold: 'Number of calls that causes the display of an alert',
        announce_agent:
          'When the agent answers the call, a message will be played to announce the identification number of the answering operator',
        announce_frequency: 'Frequency in seconds of queue announces,',
        announce_position: 'Regularly play a message to inform the caller about his current position',
        announce_waiting: 'Regularly play a message to inform the caller about the estimated hold time',
        busy_delay_time: 'Busy delay time in seconds',
        call_timeout: 'Call timeout in seconds',
        callback_caller_id_number: 'Caller id number used to make the outgoing call',
        callback_caller_number_regex: 'Regex used to valid alternative callback number dialed by caller',
        callback_delay: 'Delay before retry a callback in seconds',
        callback_enabled: 'Enable callback service',
        callback_retention: 'How long, in hours, before removing a booked callback from the queue',
        callback_retries: 'Number of callback retries',
        callback_timeout: 'How many seconds to ring for a queue callback',
        callback_username: 'Username used to make the outgoing call',
        callback_wait_time:
          'After the specified wait time in the queue, the caller is now able to press one key and request a callback',
        continue_on_answer:
          'Whether or not to continue following the call flow when a call is terminated after being answered by an agent',
        continue_on_max_waiters:
          'Whether or not to continue following the call flow when a call joins a queue and the max numbers of calls configurated is reached',
        daily_reset_stats: 'Enable the daily reset for the real time statistics',
        daily_reset_stats_time: 'Time of the day when to reset real time statistics',
        exitkey: 'Whether or not to let caller to leave queue pressing 0 key and to continue following the call flow',
        ignore_unavail: 'Ignore unreachable status of the agents',
        max_exten: 'Maximum number of people waiting in the queue. A 0 value means unlimited',
        max_wait_time: 'Time in seconds that a call will wait in the queue before it is routed to the next block',
        no_answer_delay_time: 'No answer delay time in seconds',
        reject_delay_time: 'Reject delay time in seconds',
        service_level: 'Service level stats. A 0 value means no statistics',
        skip_empty: 'Skip queue without logged agents',
        weight:
          'When compared to other queues, higher weight values get first shot at available agents when the same agent is included in more than one queue. A 0 value disables this function',
        wrap_up_time: 'Wrap up time in seconds',
      },
    },
  },
  agents: {
    name: 'Agents',
    type: 'Type',
    fields: {
      group: 'Group',
      user: 'User',
      labels: { id: 'Labels', name: 'Labels' },
      name: 'Name',
      queue: 'Queue',
      settings: {
        busy_delay_time: 'Busy delay time',
        call_timeout: 'Call timeout',
        initial_paused: 'Initially paused',
        no_answer_delay_time: 'No answer delay time',
        position: 'Position',
        reject_delay_time: 'Reject delay time',
        wrap_up_time: 'Wrap up time',
      },
    },
    operations: {
      create: 'Create agent',
      edit: 'Edit agent',
      delete: 'Delete agent',
      assign_agents: 'Assign agents',
      sections: {
        details: 'Details',
        settings: 'Settings',
      },
    },
    helperTexts: {
      bulkAssignLabels: 'List of labels to assign to newly added agents',
      settings: {
        busy_delay_time:
          'Number of seconds to wait before a call is routed to an agent if a busy tone is detected from agent’s phone',
        call_timeout: 'Number of seconds that an agent ring',
        no_answer_delay_time: 'Number of seconds to wait before a call is routed to an agent if he doesn’t answer.',
        position: 'Position of the agent in the queue.',
        reject_delay_time:
          'Number of seconds to wait before a call is routed to an agent if a reject tone is detected from agent’s phone',
        wrap_up_time: 'Number of seconds for the agent to do after-call work after the conversation is finished.',
      },
    },
  },
  'presence/users': {
    name: 'Presence users',
    fields: {
      id: 'ID',
      firstname: 'First name',
      lastname: 'Last name',
      middlename: 'Middle name',
      username: 'Username',
    },
  },
  queue_measurements: {
    name: 'Queue Stats',
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
    },
  },
  call_reports: {
    name: 'Call Reports',
  },
  call_recordings: {
    name: 'Call Recordings',
    fields: {
      caller_id_name: 'Caller ID name',
      caller_id_number: 'Caller ID number',
      called_id_name: 'Called ID name',
      called_id_number: 'Called ID number',
      start_time: 'Start time',
      end_time: 'End time',
      account: 'Account',
      id: 'ID',
      recording_uuid: 'Recording UUID',
      url: 'URL',
      user_id: 'User ID',
      status: 'Status',
      labels: { name: 'Labels', id: 'Label ID' },
    },
    statuses: {
      recording: 'Recording',
      completed: 'Completed',
      failed: 'Failed',
    },
    unavailable: 'Recording unavailable',
  },
  'me/call_recordings': {
    name: 'Call Recordings',
  },
  call_recording_confs: {
    name: 'Call Recording Configurations',
    fields: {
      name: 'Name',
      description: 'Description',
      soundfile_id: 'Announcement soundfile',
      labels: { id: 'Labels', name: 'Labels' },
      call_recording_labels: 'Call recording labels',
      settings: {
        announce: 'Play announcement',
        bip: 'Play beep sound',
        asr: 'Enable speech recognition',
        format_file: 'Recording format',
        name_file: 'Filename template',
        hash_file: 'Hash recording file',
        crypto: 'Encrypt recording',
        crypto_password: 'Encryption password',
        crypto_password_confirmation: 'Encryption password confirmation',
        ek: 'Encryption key',
        ek_recovery: 'Enable key recovery',
      },
      types: {
        in: 'Inbound',
        outbound_local: 'Outbound local',
        outbound_external: 'Outbound external',
        onetouch: 'One-touch',
      },
    },
    operations: {
      create: 'Create recording configuration',
      edit: 'Edit recording configuration',
      delete: 'Delete recording configuration',
      sections: {
        details: 'Details',
        settings: 'Settings',
        users: 'Users',
      },
      manage_users: 'Manage user assignments',
    },
  },
  user_call_recording_confs: {
    name: 'User Recording Assignments',
    fields: {
      id: 'ID',
      type: 'Call type',
      user: 'User',
    },
    confirmDelete: {
      title: 'Delete user recording assignment',
      content: 'Are you sure you want to remove this user recording assignment?',
    },
  },
  queue_call_sessions: {
    name: 'Queue Call Sessions',
    fields: {
      id: 'ID',
      answer_time: 'Answer duration (s)',
      answer_ts: 'Answer timestamp',
      answered_by: 'Answered by',
      caller_id_name: 'Caller ID name',
      caller_id_number: 'Caller ID number',
      called_id_number: 'Called ID number',
      first_queue: 'First queue',
      final_queue: 'Final queue',
      final_exit_cause: 'Final exit cause',
      first_join_ts: 'First join time',
      last_leave_ts: 'Last leave time',
      total_time: 'Total queue time (s)',
      total_queues: 'Queues traversed',
      labels: { id: 'Labels', name: 'Labels' },
      actions: 'Actions',
    },
    actions: {
      viewQueueEvents: 'Queue events',
      viewAgentEvents: 'Agent events',
    },
  },
  queue_call_session_events: {
    name: 'Queue Call Session Events',
    fields: {
      id: 'ID',
      caller_id_name: 'Caller ID name',
      caller_id_number: 'Caller ID number',
      called_id_number: 'Called ID number',
      queue_name: 'Queue name',
      join_ts: 'Join time',
      leave_ts: 'Leave time',
      exit_cause: 'Exit cause',
      answered_by: 'Answered by',
      answer_ts: 'Answer time',
      answer_time: 'Answer duration (s)',
      labels: { id: 'Labels', name: 'Labels' },
    },
  },
  queue_call_session_agent_events: {
    name: 'Queue Call Session Agent Events',
    fields: {
      id: 'ID',
      agent_name: 'Agent name',
      caller_id_name: 'Caller ID name',
      queue_name: 'Queue name',
      originate_ts: 'Originate time',
      answer_ts: 'Answer time',
      end_ts: 'End time',
      exit_cause: 'Exit cause',
      answer_time: 'Answer duration (s)',
      caller_id_number: 'Caller ID number',
      called_id_number: 'Called ID number',
      group_name: 'Group name',
      labels: { id: 'Labels', name: 'Labels' },
    },
  },
  auth_providers: {
    name: 'Auth Providers',
    description: 'View and manage authentication providers',
    fields: {
      name: 'Name',
      description: 'Description',
      client_id: 'Client ID',
      client_secret: 'Client Secret',
      configuration_endpoint: 'Configuration endpoint',
      enabled: 'Enable',
      issuer: 'Issuer',
      scopes: 'Scopes',
      labels: { id: 'Labels', name: 'Labels' },
      button_text: 'Custom label for the login button (e.g. Sign in with Google)',
      policies: { login: 'Enable Login', token_exchange: 'Enable external token exchange' },
    },
    operations: {
      create: 'Create provider',
      edit: 'Edit provider',
      delete: 'Delete provider',
      sections: {
        providerConfig: 'Configuration',
        advanced: 'Advanced',
      },
    },
    configEpValidation: {
      invalid: 'Invalid endpoint',
      valid: 'Valid endpoint',
    },
    labels: {
      enabled: 'Enabled',
      loginEnabled: 'Login allowed',
      tokenExchangeEnabled: 'Token exchange allowed',
    },
  },
  oauth_applications: {
    name: 'OAuth Applications',
    description: 'View and manage OAuth applications',
    fields: {
      name: 'Name',
      client_id: 'Client ID',
      client_secret: 'Client Secret',
      redirect_uri: 'Redirect URI',
      scopes: 'Scopes',
      enabled: 'Enable',
      labels: { id: 'Labels', name: 'Labels' },
    },
    helperTexts: {
      redirect_uri:
        'An absolute URI to which the authorization server will redirect the user-agent after granting or denying access.',
    },
    operations: {
      create: 'Create application',
      edit: 'Edit application',
      delete: 'Delete application',
      sections: {
        applicationConfig: 'Application Configuration',
      },
    },
    labels: {
      enabled: 'Enabled',
    },
  },
  call_sessions: {
    name: 'CDR',
    fields: {
      id: 'ID',
      caller_id_number: 'Caller ID number',
      caller_id_name: 'Caller ID name',
      called_id_number: 'Called ID number',
      called_id_name: 'Called ID name',
      answer_time: 'Answer time',
      creation_time: 'Creation time',
      hangup_cause: 'Hangup cause',
      hangup_time: 'Hangup time',
      account: 'Account',
      called_account: 'Called account',
      called: 'Called',
      caller: 'Caller',
      labels: { id: 'Labels', name: 'Labels' },
      hangupCauses: {
        UNALLOCATED_NUMBER: 'Unallocated number',
        NO_ROUTE_TRANSIT_NET: 'No route through transit network',
        NO_ROUTE_DESTINATION: 'No route to destination',
        CHANNEL_UNACCEPTABLE: 'Channel unacceptable',
        CALL_AWARDED_DELIVERED: 'Call awarded and delivered',
        NORMAL_CLEARING: 'Normal clearing',
        USER_BUSY: 'User busy',
        NO_USER_RESPONSE: 'No user response',
        NO_ANSWER: 'No answer',
        SUBSCRIBER_ABSENT: 'Subscriber absent',
        CALL_REJECTED: 'Call rejected',
        NUMBER_CHANGED: 'Number changed',
        REDIRECTION_TO_NEW_DESTINATION: 'Redirection to new destination',
        EXCHANGE_ROUTING_ERROR: 'Exchange routing error',
        DESTINATION_OUT_OF_ORDER: 'Destination out of order',
        INVALID_NUMBER_FORMAT: 'Invalid number format',
        FACILITY_REJECTED: 'Facility rejected',
        RESPONSE_TO_STATUS_ENQUIRY: 'Response to status enquiry',
        NORMAL_UNSPECIFIED: 'Normal unspecified',
        NORMAL_CIRCUIT_CONGESTION: 'Normal circuit congestion',
        NETWORK_OUT_OF_ORDER: 'Network out of order',
        NORMAL_TEMPORARY_FAILURE: 'Normal temporary failure',
        SWITCH_CONGESTION: 'Switch congestion',
        ACCESS_INFO_DISCARDED: 'Access information discarded',
        REQUESTED_CHAN_UNAVAIL: 'Requested channel unavailable',
        PRE_EMPTED: 'Preempted',
        FACILITY_NOT_SUBSCRIBED: 'Facility not subscribed',
        OUTGOING_CALL_BARRED: 'Outgoing call barred',
        INCOMING_CALL_BARRED: 'Incoming call barred',
        BEARERCAPABILITY_NOTAUTH: 'Bearer capability not authorized',
        BEARERCAPABILITY_NOTAVAIL: 'Bearer capability not available',
        SERVICE_UNAVAILABLE: 'Service unavailable',
        BEARERCAPABILITY_NOTIMPL: 'Bearer capability not implemented',
        CHAN_NOT_IMPLEMENTED: 'Channel not implemented',
        FACILITY_NOT_IMPLEMENTED: 'Facility not implemented',
        SERVICE_NOT_IMPLEMENTED: 'Service not implemented',
        INVALID_CALL_REFERENCE: 'Invalid call reference',
        INCOMPATIBLE_DESTINATION: 'Incompatible destination',
        INVALID_MSG_UNSPECIFIED: 'Invalid message unspecified',
        MANDATORY_IE_MISSING: 'Mandatory IE missing',
        MESSAGE_TYPE_NONEXIST: 'Non-existent message type',
        WRONG_MESSAGE: 'Wrong message',
        IE_NONEXIST: 'Non-existent IE',
        INVALID_IE_CONTENTS: 'Invalid IE contents',
        WRONG_CALL_STATE: 'Wrong call state',
        RECOVERY_ON_TIMER_EXPIRE: 'Recovery on timer expiry',
        MANDATORY_IE_LENGTH_ERROR: 'Mandatory IE length error',
        PROTOCOL_ERROR: 'Protocol error',
        INTERWORKING: 'Interworking',
        SUCCESS: 'Success',
        ORIGINATOR_CANCEL: 'Originator cancel',
        CRASH: 'Crash',
        SYSTEM_SHUTDOWN: 'System shutdown',
        LOSE_RACE: 'Race lost',
        MANAGER_REQUEST: 'Manager request',
        BLIND_TRANSFER: 'Blind transfer',
        ATTENDED_TRANSFER: 'Attended transfer',
        ALLOTTED_TIMEOUT: 'Allotted timeout',
        USER_CHALLENGE: 'User challenge',
        MEDIA_TIMEOUT: 'Media timeout',
        PICKED_OFF: 'Call picked off',
        USER_NOT_REGISTERED: 'User not registered',
        PROGRESS_TIMEOUT: 'Progress timeout',
        INVALID_GATEWAY: 'Invalid gateway',
        GATEWAY_DOWN: 'Gateway down',
        INVALID_URL: 'Invalid URL',
        INVALID_PROFILE: 'Invalid profile',
        NO_PICKUP: 'No pickup',
        SRTP_READ_ERROR: 'SRTP read error',
        BOWOUT: 'Call bowout',
        BUSY_EVERYWHERE: 'Busy everywhere',
        DECLINE: 'Call declined',
        DOES_NOT_EXIST_ANYWHERE: 'Does not exist',
        NOT_ACCEPTABLE: 'Not acceptable',
        UNWANTED: 'Unwanted',
        NO_IDENTITY: 'No identity',
        BAD_IDENTITY_INFO: 'Bad identity information',
        UNSUPPORTED_CERTIFICATE: 'Unsupported certificate',
        INVALID_IDENTITY: 'Invalid identity',
        STALE_DATE: 'Stale date',
        REJECT_ALL: 'Reject all',
      },
    },
    info: {
      title: 'Call Session Details',
    },
    errors: {
      loadFailed: 'Unable to load call session details.',
    },
  },
  call_session_events: {
    name: 'Call session events',
    fields: {
      id: 'ID',
      detailsLabel: 'Details',
      details: {
        account: 'Account',
        action: 'Action',
        answer_time: 'Answer time',
        called_account: 'Called account',
        create_time: 'Create time',
        hangup_time: 'Hangup time',
        type: 'Type',
        to: 'To',
        transferor: 'Transferor',
        description: 'Description',
        mobility_username: 'Mobility Username',
        digit: 'Digit',
        called_number: 'Called number',
        reason: 'Reason',
        result: 'Result',
        carrier_name: 'Carrier name',
      },
      parent_channel_id: 'Parent channel ID',
      channel_id: 'Channel ID',
      ts_event: 'Time of event',
      labels: { id: 'Labels', name: 'Labels' },
    },
    viewModes: {
      hierarchy: 'Detailed cards',
      tree: 'Tree view',
    },
    summary: {
      channelTitle: 'Channel %{channel}',
      parent: 'Parent channel',
      parentFallback: 'Root channel',
      unknownChannel: 'Unknown channel',
      firstEvent: 'First event',
      lastEvent: 'Last event',
      eventCount: '%{smart_count} event |||| %{smart_count} events',
    },
    event: {
      timestamp: 'Timestamp',
      noDetails: 'No additional details',
      unknownType: 'Unknown type',
    },
    types: {
      agent_call: 'Agent Call',
      user_call: 'User Call',
      outbound_call: 'Outbound Call',
      queue: 'Queue',
      mobility: 'Mobility',
      call_me: 'Call Me',
      calendar: 'Calendar',
      presence_router: 'Presence Router',
      disconnect: 'Disconnect',
    },
    status: {
      answered: 'Answered',
      unanswered: 'Unanswered',
    },
    empty: {
      title: 'No events yet',
      description: 'Call session activity will appear here once events are recorded.',
    },
    errors: {
      loadFailed: 'Unable to load call session events.',
    },
    toggleAdvanced: 'Show advanced info',
  },
  encryption_configurations: {
    name: 'Storage Encryption Keys',
    fields: {
      id: 'ID',
      domain_id: 'Domain',
      name: 'Name',
      description: 'Description',
      enabled: 'Enabled',
      labels: { id: 'Labels', name: 'Labels' },
      metadata: {
        type: 'Type',
      },
    },
    operations: {
      create: 'Create encryption configuration',
      delete: 'Delete encryption configuration',
      edit: 'Edit encryption configuration',
      sections: {
        details: 'Details',
        configuration: 'Configuration',
      },
    },
    helperTexts: {
      enabled: 'Only one configuration can be enabled at a time.',
    },
  },
  storage_configurations: {
    name: 'External Storage Configurations',
    fields: {
      id: 'ID',
      domain_id: 'Domain',
      name: 'Name',
      description: 'Description',
      enabled: 'Enabled',
      labels: { id: 'Labels', name: 'Labels' },
      metadata: {
        type: 'Type',
        endpoint_scheme: 'Endpoint Scheme',
        endpoint_host: 'Endpoint Host',
        endpoint_port: 'Endpoint Port',
        region: 'Region',
        bucket: 'Bucket',
        access_key: 'Access Key',
        secret_key: 'Secret Key',
        ssl_verify: 'Verify SSL Certificate',
      },
    },
    operations: {
      create: 'Create storage configuration',
      delete: 'Delete storage configuration',
      edit: 'Edit storage configuration',
      sections: {
        details: 'Details',
        s3_configuration: 'S3 Configuration',
      },
    },
    helperTexts: {
      enabled: 'Only one configuration can be enabled at a time.',
      immutable: 'These fields cannot be edited after saving.',
    },
  },
};
